import { platformApiFetch } from '@/services/platformApi/client';
import type { Json } from "@/integrations/supabase/types";
import { getCurrentAuthUser } from '@/services/authSession';

// Lightweight analytics event shape and window augmentation
export type AnalyticsEvent = { event?: string } & Record<string, unknown>;
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: AnalyticsEvent[];
  }
}

const isBrowser = typeof window !== 'undefined' && typeof sessionStorage !== 'undefined' && typeof navigator !== 'undefined';

// Generate a session ID that persists for the browser session
const getSessionId = (): string => {
  if (!isBrowser) {
    return 'server-render';
  }

  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Get basic browser info for analytics
const getBrowserInfo = () => {
  return {
    user_agent: isBrowser ? navigator.userAgent : 'server-render',
  };
};

export class AnalyticsService {
  private static sessionId = getSessionId();
  private static sessionStartTime = Date.now();
  private static pagesVisited = 0;
  private static lastActivityTime = Date.now();
  private static sessionUpdateInterval: NodeJS.Timeout | null = null;
  
  // Initialize session tracking
  static initializeSession() {
    // Set up periodic session updates
    if (!this.sessionUpdateInterval) {
      this.sessionUpdateInterval = setInterval(() => {
        this.updateSessionActivity();
      }, 30000); // Update every 30 seconds
    }

    // Handle page unload to properly close session
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.endSession();
      });

      // Handle visibility change (when user switches tabs)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.lastActivityTime = Date.now();
        }
      });
    }
  }

  // Update session activity
  private static async updateSessionActivity() {
    try {
      const now = Date.now();
      const sessionDuration = Math.floor((now - this.sessionStartTime) / 1000);

      await platformApiFetch(`/v1/analytics/session/${this.sessionId}`, {
        method: 'PUT',
        body: JSON.stringify({
          last_activity: new Date().toISOString(),
          session_duration_seconds: sessionDuration,
          pages_visited: this.pagesVisited,
        }),
      });
        
    } catch (error: unknown) {
      console.warn('Error updating session activity:', error);
    }
  }

  // End the current session
  private static async endSession() {
    try {
      const sessionDuration = Math.floor((Date.now() - this.sessionStartTime) / 1000);

      await platformApiFetch(`/v1/analytics/session/${this.sessionId}`, {
        method: 'PUT',
        body: JSON.stringify({
          session_duration_seconds: sessionDuration,
          pages_visited: this.pagesVisited,
          last_activity: new Date().toISOString(),
        }),
      });

      if (this.sessionUpdateInterval) {
        clearInterval(this.sessionUpdateInterval);
        this.sessionUpdateInterval = null;
      }
    } catch (error: unknown) {
      console.warn('Error ending session:', error);
    }
  }

  // Track provider impression (when provider card comes into view)
  static async trackProviderImpression(providerId: string, context: {
    pageType: 'explore' | 'category' | 'search' | 'home' | 'favorites';
    searchQuery?: string;
    category?: string;
  }) {
    try {
      const user = await getCurrentAuthUser();

      await platformApiFetch('/v1/analytics/provider-impression', {
        method: 'POST',
        body: JSON.stringify({
          provider_id: providerId,
          user_id: user?.id || null,
          session_id: this.sessionId,
          page_type: context.pageType,
          search_query: context.searchQuery || null,
          category: context.category || null,
          ...getBrowserInfo(),
        }),
      });
        
    } catch (error: unknown) {
      console.error('Error tracking provider impression:', error);
    }
  }

  // Track provider profile view
  static async trackProviderView(providerId: string, referrerUrl?: string) {
    try {
      const user = await getCurrentAuthUser();

      await platformApiFetch('/v1/analytics/provider-view', {
        method: 'POST',
        body: JSON.stringify({
          provider_id: providerId,
          user_id: user?.id || null,
          session_id: this.sessionId,
          referrer_url: referrerUrl || document.referrer || null,
          ...getBrowserInfo(),
        }),
      });
        
    } catch (error: unknown) {
      console.error('Error tracking provider view:', error);
    }
  }

  // Track contact attempt (WhatsApp, phone, website)
  static async trackProviderContact(providerId: string, contactType: 'whatsapp' | 'phone' | 'website') {
    try {
      const user = await getCurrentAuthUser();

      await platformApiFetch('/v1/analytics/provider-contact', {
        method: 'POST',
        body: JSON.stringify({
          provider_id: providerId,
          user_id: user?.id || null,
          session_id: this.sessionId,
          contact_type: contactType,
          ...getBrowserInfo(),
        }),
      });
        
    } catch (error: unknown) {
      console.error('Error tracking provider contact:', error);
    }
  }

  // Track search query
  static async trackSearch(params: {
    searchQuery?: string;
    category?: string;
    // Accept any JSON-serializable value
    filtersApplied?: Json | null;
    resultsCount: number;
  }) {
    try {
      const user = await getCurrentAuthUser();

      await platformApiFetch('/v1/analytics/search', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user?.id || null,
          session_id: this.sessionId,
          search_query: params.searchQuery || null,
          category: params.category || null,
          filters_applied: params.filtersApplied || null,
          results_count: params.resultsCount,
          ...getBrowserInfo(),
        }),
      });
        
    } catch (error: unknown) {
      console.error('Error tracking search:', error);
    }
  }

  // Track session with proper page visit incrementing
  static async trackSession(params: {
    landingPage?: string;
    referrerUrl?: string;
  }) {
    try {
      const user = await getCurrentAuthUser();
      
      // Increment pages visited for this session
      this.pagesVisited++;
      this.lastActivityTime = Date.now();

      const sessionDuration = Math.floor((Date.now() - this.sessionStartTime) / 1000);

      await platformApiFetch('/v1/analytics/session', {
        method: 'POST',
        body: JSON.stringify({
          session_id: this.sessionId,
          user_id: user?.id || null,
          landing_page: params.landingPage || window.location.pathname,
          referrer_url: params.referrerUrl || document.referrer || null,
          pages_visited: this.pagesVisited,
          last_activity: new Date().toISOString(),
          session_duration_seconds: sessionDuration,
          ...getBrowserInfo(),
        }),
      });
      
    } catch (error: unknown) {
      console.warn('Error tracking session (non-critical):', error);
    }
  }

  // Clean up old sessions (call this periodically)
  static async cleanupOldSessions() {
    try {
      await platformApiFetch('/v1/analytics/session-cleanup', {
        method: 'POST',
      });
        
    } catch (error: unknown) {
      console.warn('Error cleaning up old sessions:', error);
    }
  }
}
